<template>
  <Popover
    placement="left-end"
    size="sm"
    variant="warning"
    :isDisabled="isUnableDownload || isCompleteData"
  >
    <template slot="popover">
      取得中のデータがあります。<br />しばらくお待ちください。<br />※ダウンロードは可能ですが、取得中のデータは反映されません。
    </template>
    <div class="p-contentplans-manual-button-area">
      <Button
        fsize="f12"
        variant="download_gy"
        radius
        class="l-w-380"
        :class="{ 'is-loading': isProcessing }"
        :disabled="
          isProcessing || isUnableDownload || !isAfterManualReleaseDate
        "
        @click="download"
      >
        <Icon2 v-if="isCompleteData" variant="download" :size="12" />
        <Icon2 v-else variant="warning" :size="12" />
        <span>調査したキーワードの記事作成マニュアルをダウンロード</span>
      </Button>
      <div v-if="!isAfterManualReleaseDate" class="c-text--alert">
        マニュアルをダウンロードしたい場合は、再調査をしてください。
      </div>
    </div>
    <ContentManualContainer
      v-if="isActive"
      :createdAt="createdAt"
      :keyword="keyword"
      :project="project"
      :tdhWordsItems="tdhWordsItems"
    />
  </Popover>
</template>

<script>
import Amplitude from '@/amplitude';
import Button from '@/components/Button';
import Icon2 from '@/components/Icon2';
import ContentManualContainer from '@/components/contentPlans/ContentManualContainer';
import Popover from '@/components/Popover';
import utils from '@/utils';
import axios from 'axios';
import ContentPlansCss from '!!to-string-loader!css-loader!@/assets/css/download_content_plans.output.css';
export default {
  props: {
    project: {
      type: Object,
      required: true
    },
    createdAt: {
      type: String,
      required: true
    },
    isUnableDownload: {
      type: Boolean,
      required: true
    },
    isCompleteData: {
      type: Boolean,
      required: true
    },
    isAfterManualReleaseDate: {
      type: Boolean,
      required: true
    },
    keyword: {
      type: String,
      required: true
    },
    tdhWordsItems: {
      type: Array,
      required: true
    }
  },
  components: {
    Button,
    Icon2,
    ContentManualContainer,
    Popover
  },
  data() {
    return {
      isProcessing: false,
      isActive: false
    };
  },
  methods: {
    async download() {
      if (this.isProcessing || !this.isAfterManualReleaseDate) {
        return;
      }
      this.isProcessing = true;
      this.isActive = true;

      this.$nextTick(async () => {
        const now = utils.formatDate(new Date(), 'YYYY年MM月DD日');
        const keywordForTitle =
          this.keyword.length > 50 ? this.keyword.slice(0, 50) : this.keyword;
        const title = `記事作成マニュアル_${keywordForTitle}_${now}_${this.project.name}`;

        const styles = this.getStyles();
        const contents = await this.getContents();
        const scripts = this.getScripts();

        const data = `<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8"><title>${title}</title>${styles}</head><body>${contents}${scripts}</body></html>`;

        const fileName = `${title}.html`;
        utils.downloadFile(data, fileName);

        this.isProcessing = false;
        this.isActive = false;

        const properties = {
          keyword: this.keyword
        };
        Amplitude.postLogEvent('ManualHtmlDownload - ContentPlan', properties);
      });
    },
    async getSvgSprite(svgUrl, svgSymbolIds) {
      try {
        const data = await axios.get(svgUrl).then(res => res.data);
        const parser = new DOMParser();
        const doc = parser.parseFromString(data, 'image/svg+xml');
        const svg = doc.querySelector('svg');

        // 必要なsymbol以外を削除する
        svg.querySelectorAll('symbol').forEach(symbol => {
          if (!svgSymbolIds.includes(symbol.id)) {
            symbol.remove();
          }
        });

        const div = document.createElement('div');
        div.appendChild(svg);
        div.style.display = 'none';

        return div.outerHTML;
      } catch (error) {
        return '';
      }
    },
    async getContents() {
      const block = document
        .querySelector('.p-content-plan-manual')
        .cloneNode(true);

      // data-include-manual-download属性を削除する
      block.querySelectorAll('[data-include-manual-download]').forEach(el => {
        el.removeAttribute('data-include-manual-download');
      });

      // 省略表示されたテキストをツールチップで表示するための仕込み
      block.querySelectorAll('.c-text--ellipsisTip').forEach(el => {
        const p = el.querySelector('p');
        // HTMLダウンロード時点で省略表示されている場合のみツールチップ用のspanが存在する
        // spanがあったりなかったりするので、spanを確実に存在させる
        el.innerHTML = `${p.outerHTML}<span>${p.textContent}</span>`;
      });

      // perfect-scrollbarのお掃除
      block.querySelectorAll('.ps').forEach(el => {
        el.querySelector('.ps__rail-x').remove();
        el.querySelector('.ps__rail-y').remove();
        el.classList.remove('ps', 'ps--active-y');
      });

      // リロードを促すテキストとリロードボタンを削除する
      block.querySelectorAll('.c-error').forEach(el => {
        const title = el.querySelector('.c-error__title');
        const html = title.innerHTML.replace(
          /(<br>)?\s*ページを再読み込みしてください。/,
          ''
        );
        title.innerHTML = html;
        el.querySelector('.c-btn').remove();
      });

      // 画像をbase64に変換して埋め込む
      const imgs = Array.from(block.querySelectorAll('img'));

      await Promise.all(
        imgs.map(async img => {
          try {
            const response = await axios.get(img.src, { responseType: 'blob' });
            const base64 = await this.loadFile(response.data);
            img.src = base64;
          } catch (error) {
            img.remove();
          }
        })
      );

      // SVGスプライトしているSVGを埋め込む
      let svgSprite = '';
      let svgUrl = '';
      let svgSymbolIds = [];

      block.querySelectorAll('svg').forEach(svg => {
        const use = svg.querySelector('use');
        const href = use.getAttribute('xlink:href');
        const matches = href.match(/(.*sprite.*\.svg)#(.+)/);
        if (matches) {
          svgUrl = matches[1];
          svgSymbolIds.push(matches[2]);
          use.setAttribute('xlink:href', `#${matches[2]}`);
        }
      });

      if (svgUrl) {
        svgSymbolIds = Array.from(new Set(svgSymbolIds)); //重複削除
        svgSprite = await this.getSvgSprite(svgUrl, svgSymbolIds);
      }

      return `${block.outerHTML}${svgSprite}`;
    },
    getStyles() {
      const contentPlansCssText = ContentPlansCss.toString();
      return `<style>${contentPlansCssText}</style>`;
    },
    getScripts() {
      // 省略表示されたテキストをツールチップで表示する
      const tooltip = `
        document.querySelectorAll('.c-text--ellipsisTip').forEach((el) => {
          const p = el.querySelector('p');
          const span = p.nextSibling;
          const show = () => { span.style.display = 'block'; }
          const hide = () => { span.style.display = 'none'; }

          el.addEventListener('mouseenter', () => {
            if (p.scrollWidth > p.offsetWidth) {
              show();
            } else {
              hide();
            }
          });
          el.addEventListener('mouseleave', () => {
            hide();
          });
        });`;

      // eslint-disable-next-line no-useless-escape
      return `<script>${tooltip}<\/script>`;
    },
    loadFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject();
      });
    }
  }
};
</script>
